import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import Spinner from "../ui/Spinner/Spinner";
import Alert from "../ui/alert/Alert";
import { CreateWalletLinkModal } from "./Modals/CreateWalletLinkModal";
import { ViewWalletLinkModal } from "./Modals/ViewWalletLinkModal";
import { listWalletLinks } from "./API/services";
import { WalletLinkItem, WalletLinkStatus } from "./types";
import CurrencyFlag from "./CurrencyFlag";

const STATUS_STYLES: Record<WalletLinkStatus, string> = {
  ACTIVE:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  PROCESSING:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  PAID: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  CANCELLED:
    "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  EXPIRED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  DECLINED:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

function WalletPaymentLinks() {
  const [links, setLinks] = useState<WalletLinkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError("");
      setLinks(await listWalletLinks());
    } catch (err: any) {
      setError(err?.message || "Could not load your payment links.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Wallet Payment Links
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Let anyone pay into your wallet with a link. No account needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700 active:scale-95 dark:bg-blue-700 dark:hover:bg-blue-600"
        >
          Create link
        </button>
      </div>

      {error && <Alert variant="error" title="Error" message={error} />}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-10 w-10 text-blue-500" />
        </div>
      ) : links.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
          You haven't created any payment links yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">For</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-gray-100 last:border-0 dark:border-gray-700"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    <span className="inline-flex items-center gap-2">
                      <CurrencyFlag src={l.flag} alt={l.currency} />
                      {l.amount.toLocaleString()} {l.currency}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {l.payerName || "-"}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {l.rail === "MOMO" ? "Mobile money" : "Bank transfer"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        STATUS_STYLES[l.status]
                      }`}
                    >
                      {l.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {format(new Date(l.expiresAt), "MMM d, yyyy h:mm a")}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {format(new Date(l.createdAt), "MMM d, yyyy")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setViewId(l.id)}
                      className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white transition-all duration-200 hover:bg-blue-700 active:scale-95 dark:bg-blue-700 dark:hover:bg-blue-600"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateWalletLinkModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />

      <ViewWalletLinkModal
        isOpen={!!viewId}
        linkId={viewId}
        onClose={() => setViewId(null)}
        onChanged={load}
      />
    </div>
  );
}

export default WalletPaymentLinks;
